import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Check, Play, Gift, Trophy, Lock } from 'lucide-react';
import { getInnerShiftProgress } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import ChallengePicker from './ChallengePicker';

/**
 * Full 21-Day Inner Shift hero card for the Dashboard.
 * Shows progress, today's track, 21-day dots with checkmarks,
 * streak, reward callout, and celebration modal on completion.
 */
export default function InnerShiftCard({ variant = 'full' }) {
  const { user } = useAuth();
  const isEmbedded = variant === 'embedded';
  const tier = user?.auth_provider === 'email_lead' ? 'lead' : 'full';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('lt_shift_dismissed') === '1');

  const reload = async () => {
    try {
      const res = await getInnerShiftProgress();
      setData(res.data);
      if (res.data?.badge_earned && !localStorage.getItem('lt_inner_shift_seen')) {
        setShowCelebration(true);
        localStorage.setItem('lt_inner_shift_seen', '1');
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    (async () => {
      await reload();
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <div className={`rounded-2xl bg-zinc-900/40 border border-white/5 h-56 animate-pulse ${isEmbedded ? '' : 'col-span-1 md:col-span-2 lg:col-span-4'}`} />
    );
  }

  // User has not picked a challenge yet — show picker
  if (data && !data.challenge_chosen && !data.badge_earned && !dismissed) {
    return (
      <ChallengePicker
        tier={tier}
        compact={isEmbedded}
        onStart={async () => {
          setLoading(true);
          await reload();
          setLoading(false);
        }}
        onDismiss={isEmbedded ? undefined : () => {
          localStorage.setItem('lt_shift_dismissed', '1');
          setDismissed(true);
        }}
      />
    );
  }

  if (!data?.today_track && !data?.badge_earned) {
    return null;
  }

  const {
    current_day = 1,
    total_days = 21,
    completed_count = 0,
    completed_days = [],
    today_track,
    today_track_completed,
    current_streak = 0,
    badge_earned,
    reward_unlocked,
  } = data;

  const pct = Math.round((completed_count / total_days) * 100);
  const daysLeft = Math.max(0, total_days - completed_count);

  return (
    <>
      <div
        className={`relative rounded-2xl overflow-hidden border border-amber-500/20 bg-gradient-to-br from-amber-900/25 via-zinc-900 to-zinc-950 p-5 md:p-6 ${
          isEmbedded ? 'mb-6' : 'col-span-1 md:col-span-2 lg:col-span-4'
        }`}
        data-testid="inner-shift-card"
      >
        <div className="absolute top-0 right-0 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-violet-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Flame className="w-3.5 h-3.5 text-amber-400" />
              </div>
              <span className="text-[10px] tracking-[0.2em] uppercase text-amber-300/80">
                {total_days}-Day Inner Shift
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-serif text-white leading-tight">
              Day {Math.min(current_day, total_days)} of {total_days}
            </h2>
            <p className="text-xs text-zinc-400 mt-1">
              {badge_earned
                ? 'Program complete — reward unlocked'
                : daysLeft === 1
                ? 'Your final day awaits'
                : `You're ${daysLeft} days away from your inner shift`}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="flex items-center justify-end gap-1.5 text-amber-400">
                <Flame className="w-3.5 h-3.5" />
                <span className="text-lg font-serif text-white">{current_streak}</span>
              </div>
              <p className="text-[9px] text-zinc-500 uppercase tracking-wider">streak</p>
            </div>
            <div className="h-8 w-px bg-white/10" />
            <div className="text-right">
              <p className="text-lg font-serif text-white">{completed_count}/{total_days}</p>
              <p className="text-[9px] text-zinc-500 uppercase tracking-wider">completed</p>
            </div>
          </div>
        </div>

        {/* Calendar (left) + Today's track (right) */}
        <div className="flex flex-col md:flex-row md:items-center md:gap-8 mb-4">
          {/* 21 Day journey line — start -> finish */}
          <div
            className="flex-1 min-w-0 mb-5 md:mb-0"
            data-testid="inner-shift-day-dots"
          >
            <div className="flex items-center justify-between text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-3 px-1">
              <span>Start</span>
              <span className="text-amber-300/80">Day {Math.min(current_day, total_days)} · {completed_count}/{total_days}</span>
              <span>Finish</span>
            </div>

            <div className="relative py-3">
              {/* Base track */}
              <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] bg-zinc-800 rounded-full" />
              {/* Filled progress */}
              <div
                className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] bg-gradient-to-r from-amber-400 via-amber-300 to-violet-400 rounded-full shadow-[0_0_8px_rgba(251,191,36,0.6)] transition-all duration-700"
                style={{ width: `${Math.max(0, Math.min(100, ((Math.max(0, current_day - 1)) / (total_days - 1)) * 100))}%` }}
              />
              {/* Dots */}
              <div className="relative flex justify-between">
                {Array.from({ length: total_days }).map((_, i) => {
                  const day = i + 1;
                  const done = completed_days.includes(day);
                  const isToday = day === current_day && !today_track_completed;
                  return (
                    <div
                      key={day}
                      className="relative flex flex-col items-center"
                      data-testid={`shift-day-${day}`}
                    >
                      <div
                        className={`rounded-full border-2 transition-all ${
                          done
                            ? 'w-3 h-3 bg-amber-400 border-amber-300 shadow-[0_0_8px_rgba(251,191,36,0.6)]'
                            : isToday
                            ? 'w-4 h-4 bg-zinc-950 border-amber-400 animate-pulse shadow-[0_0_10px_rgba(251,191,36,0.5)]'
                            : 'w-2 h-2 bg-zinc-900 border-zinc-700'
                        }`}
                      />
                      {/* Milestone labels every 7 days */}
                      {(day === 1 || day === 7 || day === 14 || day === 21) && (
                        <span
                          className={`absolute top-5 text-[9px] tracking-widest ${
                            day <= current_day ? 'text-amber-300' : 'text-zinc-600'
                          }`}
                        >
                          {day}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Today's track CTA — sits to the right on desktop */}
          {today_track && !badge_earned && (
            <Link
              to={`/player/${today_track.id}`}
              className="flex-shrink-0 md:w-96 flex items-center gap-4 p-4 rounded-xl bg-zinc-900/60 border border-white/5 hover:border-amber-500/30 transition-colors group"
              data-testid="inner-shift-today-cta"
            >
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden flex-shrink-0 bg-zinc-800">
                {today_track.image_url && (
                  <img src={today_track.image_url} alt={today_track.title} className="w-full h-full object-cover" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] tracking-widest uppercase text-amber-400 mb-1">
                  {today_track_completed ? 'Done today — come back tomorrow' : `Today · Day ${current_day}`}
                </p>
                <h3 className="text-lg font-medium text-white truncate">{today_track.title}</h3>
                <p className="text-xs text-zinc-500 capitalize truncate">{today_track.category}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-400 text-zinc-950 flex items-center justify-center group-hover:scale-105 transition-transform flex-shrink-0">
                {today_track_completed ? (
                  <Check className="w-5 h-5" strokeWidth={3} />
                ) : (
                  <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
                )}
              </div>
            </Link>
          )}
        </div>

        {/* Reward preview */}
        {!badge_earned && (
          <div className="mt-4 flex items-center gap-2 text-xs text-zinc-500">
            <Gift className="w-3.5 h-3.5 text-amber-400" />
            <span>Complete all {total_days} days to unlock a reward track + Inner Shift badge</span>
          </div>
        )}

        {/* Completed state */}
        {badge_earned && (
          <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-amber-500/15 to-violet-500/15 border border-amber-500/30 flex items-center gap-3">
            <Trophy className="w-6 h-6 text-amber-400" />
            <div className="flex-1">
              <p className="text-sm text-white font-medium">Inner Shift unlocked</p>
              <p className="text-xs text-zinc-400">Your reward track & badge are in your profile.</p>
            </div>
            {reward_unlocked && (
              <Lock className="w-4 h-4 text-amber-400" />
            )}
          </div>
        )}
      </div>

      {/* Celebration modal */}
      {showCelebration && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
          onClick={() => setShowCelebration(false)}
          data-testid="inner-shift-celebration"
        >
          <div
            className="relative max-w-md w-full rounded-3xl bg-gradient-to-br from-amber-500/20 via-zinc-900 to-violet-900/20 border border-amber-500/30 p-8 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-5">
              <Trophy className="w-10 h-10 text-amber-400" />
            </div>
            <p className="text-xs tracking-[0.3em] uppercase text-amber-300 mb-2">{total_days} days complete</p>
            <h2 className="text-3xl font-serif text-white mb-3">Your Inner Shift is here.</h2>
            <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
              You showed up {total_days} days in a row. That's how real rewiring happens. Your reward track is now permanently unlocked, and your badge is live on your profile.
            </p>
            <button
              onClick={() => {
                setShowCelebration(false);
                toast.success('Badge added to your profile');
              }}
              className="px-6 py-3 rounded-full bg-white text-zinc-950 font-medium text-sm hover:bg-zinc-100 transition-colors"
              data-testid="inner-shift-celebration-close"
            >
              Continue your practice
            </button>
          </div>
        </div>
      )}
    </>
  );
}
